import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Autocomplete,
  Card, CardContent, Button, Alert,
  CircularProgress, Stepper, Step, StepLabel
} from '@mui/material';
import { Vaccines } from '@mui/icons-material';
import { getDogs, getInventory, recordVaccination, dispenseVaccine } from '../api/client';
import { queueAction } from '../utils/offlineQueue';

const steps = ['Select Dog', 'Select Vaccine', 'Confirm'];

export default function Vaccinate() {
  const [activeStep, setActiveStep] = useState(0);
  const [dogs, setDogs] = useState([]);
  const [vaccines, setVaccines] = useState([]);
  const [selectedDog, setSelectedDog] = useState(null);
  const [selectedVaccine, setSelectedVaccine] = useState(null);
  const [notes, setNotes] = useState('');
  const [nextDue, setNextDue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    getDogs().then(res => setDogs(res.data)).catch(console.error);
    getInventory().then(res =>
      setVaccines(res.data.filter(v => !v.expired && v.quantity > 0))
    ).catch(console.error);
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    const payload = {
      vaccine_id: selectedVaccine.id,
      notes,
      next_due_date: nextDue ? new Date(nextDue).toISOString() : null
    };
    try {
      if (!isOnline) {
        queueAction('RECORD_VACCINATION', { dogId: selectedDog.id, ...payload });
        setSuccess(true);
        return;
      }
      await recordVaccination(selectedDog.id, payload);
      await dispenseVaccine(selectedVaccine.id, 1);
      setSuccess(true);
    } catch (err) {
      if (!isOnline) {
        queueAction('RECORD_VACCINATION', { dogId: selectedDog.id, ...payload });
        setSuccess(true);
      } else {
        setError(err.response?.data?.detail || 'Failed to record vaccination');
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setActiveStep(0);
    setSelectedDog(null);
    setSelectedVaccine(null);
    setNotes('');
    setNextDue('');
    setSuccess(false);
    setError('');
  };

  if (success) return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Vaccines sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      <Typography variant="h6" fontWeight={700} color="success.main">
        Vaccination Recorded!
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        {selectedDog?.name} — {selectedVaccine?.name}
        {!isOnline && ' (saved offline, will sync when connected)'}
      </Typography>
      <Button variant="contained" fullWidth onClick={reset}>
        Record Another
      </Button>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Record Vaccination
      </Typography>

      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are offline. Records will be saved and synced when connection returns.
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Which dog was vaccinated?
            </Typography>
            <Autocomplete
              options={dogs}
              getOptionLabel={(d) => `${d.name} — ${d.breed} (${d.owner?.name})`}
              value={selectedDog}
              onChange={(_, v) => setSelectedDog(v)}
              renderInput={(params) => (
                <TextField {...params} label="Search dog" fullWidth />
              )}
            />
            <Button
              variant="contained" fullWidth sx={{ mt: 2 }}
              disabled={!selectedDog}
              onClick={() => setActiveStep(1)}
            >
              Next
            </Button>
          </CardContent>
        </Card>
      )}

      {activeStep === 1 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Which vaccine was administered?
            </Typography>
            <Autocomplete
              options={vaccines}
              getOptionLabel={(v) =>
                `${v.name} — Batch ${v.batch_number} (${v.quantity} left)`
              }
              value={selectedVaccine}
              onChange={(_, v) => setSelectedVaccine(v)}
              renderInput={(params) => (
                <TextField {...params} label="Select vaccine" fullWidth />
              )}
            />
            <TextField
              fullWidth label="Next due date (optional)"
              type="date" value={nextDue}
              onChange={(e) => setNextDue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth label="Notes (optional)"
              multiline rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button fullWidth onClick={() => setActiveStep(0)}>Back</Button>
              <Button
                variant="contained" fullWidth
                disabled={!selectedVaccine}
                onClick={() => setActiveStep(2)}
              >
                Next
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeStep === 2 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Confirm vaccination
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Dog</Typography>
              <Typography fontWeight={600}>
                {selectedDog?.name} — {selectedDog?.breed}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Vaccine</Typography>
              <Typography fontWeight={600}>{selectedVaccine?.name}</Typography>
              <Typography variant="body2">Batch: {selectedVaccine?.batch_number}</Typography>
            </Box>
            {nextDue && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Next due</Typography>
                <Typography fontWeight={600}>{nextDue}</Typography>
              </Box>
            )}
            {notes && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Notes</Typography>
                <Typography>{notes}</Typography>
              </Box>
            )}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button fullWidth onClick={() => setActiveStep(1)}>Back</Button>
              <Button
                variant="contained" fullWidth color="success"
                onClick={handleSubmit} disabled={loading}
              >
                {loading
                  ? <CircularProgress size={24} color="inherit" />
                  : 'Confirm'
                }
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
